import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import './ActivityCard.css';
import { useAgentModal } from '../../context/AgentModalContext';
import FormattedReflection from './FormattedReflection';
import { TaskReflectionAgentService } from '../../services/llmClient';
import {
  updateActivity,
  updateActivityTimer,
  saveTaskReflection,
  updateTaskTimeSpent,
} from '../../services/supabaseClient/activitiesService';
import { fetchProjectById } from '../../services/supabaseClient/projectService';
import { fetchCourseByIdWithUnits } from '../../services/supabaseClient/courseService';
import {
  startTimer,
  pauseTimer,
  resumeTimer,
  stopTimer,
  selectTimerForActivity,
  calculateTimeRemaining,
  extendTimerByMinutes,
} from '../../redux/taskTimerSlice';
import { updateActivity as updateActivityRedux } from '../../redux/activitiesSlice';
import { normalizeTaskReflectionText } from '../../utils/taskReflectionNormalize';

// Simple in-memory caches to avoid refetching names for the same IDs
const projectNameCache = new Map();
const courseNameCache = new Map();

const ActivityCard = ({ activity, onToggleComplete, onDelete }) => {
  const dispatch = useDispatch();
  const { openAgentModal } = useAgentModal();
  const timerState = useSelector((state) => selectTimerForActivity(state, activity.id));

  const [showTimeInput, setShowTimeInput] = useState(false);
  const [estimatedMinutesInput, setEstimatedMinutesInput] = useState('');
  const [needsAttention, setNeedsAttention] = useState(false);
  const [showReflectionModal, setShowReflectionModal] = useState(false);
  const [isGeneratingReflection, setIsGeneratingReflection] = useState(false);
  const [generatedReflection, setGeneratedReflection] = useState(null);
  const [actualTimeSpent, setActualTimeSpent] = useState(0);
  const [isEditingReflection, setIsEditingReflection] = useState(false);
  const [manualReflection, setManualReflection] = useState('');
  const [isSavingReflection, setIsSavingReflection] = useState(false);
  const [linkedProjectName, setLinkedProjectName] = useState(null);
  const [linkedCourseName, setLinkedCourseName] = useState(null);
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [editedDescription, setEditedDescription] = useState('');
  const [isEditingDueDate, setIsEditingDueDate] = useState(false);
  const [editedDueDate, setEditedDueDate] = useState('');
  // eslint-disable-next-line no-unused-vars
  const [currentTime, setCurrentTime] = useState(Date.now()); // Force re-render every second
  const timerIntervalRef = useRef(null);
  const audioRef = useRef(null);

  // Removed unused getUrgencyConfig function

  // Get timer values from Redux or fallback to database/defaults
  const timerActive = timerState?.timerActive || false;
  const timerPaused = timerState?.timerPaused || false;
  const estimatedMinutes = timerState?.estimatedMinutes || activity.estimated_minutes || 0;

  // Calculate time remaining based on actual elapsed time (not interval ticks)
  const timeRemainingSeconds = timerState
    ? calculateTimeRemaining(timerState)
    : (activity.time_remaining_seconds ?? null);

  // Load initial input from database on mount
  useEffect(() => {
    if (activity.estimated_minutes && !estimatedMinutesInput) {
      setEstimatedMinutesInput(activity.estimated_minutes.toString());
    }
  }, [activity.estimated_minutes, estimatedMinutesInput]);

  // Resolve linked project/course names from IDs
  useEffect(() => {
    let isMounted = true;

    const loadLinkedNames = async () => {
      // Project name
      if (activity.projectId) {
        const cachedProject = projectNameCache.get(activity.projectId);
        if (cachedProject) {
          if (isMounted) setLinkedProjectName(cachedProject);
        } else {
          try {
            const { data, error } = await fetchProjectById(activity.projectId);
            if (!error && data?.title) {
              projectNameCache.set(activity.projectId, data.title);
              if (isMounted) setLinkedProjectName(data.title);
            }
          } catch (err) {
            // eslint-disable-next-line no-console
            console.error('Failed to fetch linked project name:', err);
          }
        }
      }

      // Course name
      if (activity.courseId) {
        const cachedCourse = courseNameCache.get(activity.courseId);
        if (cachedCourse) {
          if (isMounted) setLinkedCourseName(cachedCourse);
        } else {
          try {
            const { data, error } = await fetchCourseByIdWithUnits(activity.courseId);
            if (!error && data?.title) {
              courseNameCache.set(activity.courseId, data.title);
              if (isMounted) setLinkedCourseName(data.title);
            }
          } catch (err) {
            // eslint-disable-next-line no-console
            console.error('Failed to fetch linked course name:', err);
          }
        }
      }
    };

    if (activity.projectId || activity.courseId) {
      loadLinkedNames();
    }

    return () => {
      isMounted = false;
    };
  }, [activity.projectId, activity.courseId]);

  // Persist timer state to database every 10 seconds
  useEffect(() => {
    // Don't persist timer state for completed tasks
    if (activity.completed) {
      return;
    }

    if (timerActive && !timerPaused && timeRemainingSeconds !== null) {
      const persistInterval = setInterval(async () => {
        try {
          await updateActivityTimer(
            activity.id,
            estimatedMinutes ? parseInt(estimatedMinutes) : null,
            timeRemainingSeconds
          );
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Failed to persist timer state:', error);
        }
      }, 10000); // Persist every 10 seconds

      return () => clearInterval(persistInterval);
    }
  }, [
    timerActive,
    timerPaused,
    timeRemainingSeconds,
    estimatedMinutes,
    activity.id,
    activity.completed,
  ]);

  // Timer effect - triggers re-render every second to update display
  useEffect(() => {
    // Don't run timer intervals for completed tasks to prevent memory issues on mobile
    if (activity.completed) {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
      return;
    }

    if (timerActive && !timerPaused) {
      timerIntervalRef.current = setInterval(() => {
        // Force re-render to recalculate time remaining
        setCurrentTime(Date.now());

        // Check if time ran out and task is not done
        const remaining = calculateTimeRemaining(timerState);
        if (remaining !== null && remaining <= 0 && !activity.completed && !needsAttention) {
          setNeedsAttention(true);
          // Play beeping alarm sound
          if (audioRef.current) {
            audioRef.current.loop = true; // Loop the beeping
            audioRef.current.play().catch((err) => {
              // eslint-disable-next-line no-console
              console.log('Audio play failed:', err);
            });
          }
        }
      }, 1000);
    } else {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
    }

    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
    };
  }, [timerActive, timerPaused, timerState, activity.completed, needsAttention]);

  const handleStartTimer = () => {
    setShowTimeInput(true);
  };

  const handleConfirmStart = () => {
    const minutes = parseInt(estimatedMinutesInput) || 0;
    if (minutes > 0) {
      dispatch(startTimer({ activityId: activity.id, estimatedMinutes: minutes }));
    }
    setShowTimeInput(false);
  };

  const handlePauseTimer = () => {
    dispatch(pauseTimer({ activityId: activity.id }));
  };

  const handleResumeTimer = () => {
    dispatch(resumeTimer({ activityId: activity.id }));
  };

  const handleStopAlarm = () => {
    // Stop the beeping alarm without finishing the task
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current.loop = false;
    }
    setNeedsAttention(false);
  };

  const handleFinishTimer = () => {
    // Stop the beeping alarm if playing
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current.loop = false;
    }

    const initialSeconds = estimatedMinutes ? parseInt(estimatedMinutes) * 60 : 0;
    const elapsedTime = initialSeconds - (timeRemainingSeconds || 0);
    const totalMinutes = Math.floor(elapsedTime / 60);

    // eslint-disable-next-line no-console
    console.log(`Task done in ${totalMinutes} minutes`);

    // Clear Redux timer state
    dispatch(stopTimer({ activityId: activity.id }));
    setEstimatedMinutesInput('');

    onToggleComplete(activity.id);
  };

  const handleExtendFiveMinutes = async (e) => {
    e.stopPropagation();

    dispatch(extendTimerByMinutes({ activityId: activity.id, extraMinutes: 5 }));

    try {
      const newEstimatedMinutes = (estimatedMinutes || 0) + 5;
      const newRemainingSeconds =
        (timeRemainingSeconds !== null ? timeRemainingSeconds : 0) + 5 * 60;

      await updateActivityTimer(
        activity.id,
        parseInt(newEstimatedMinutes, 10),
        newRemainingSeconds
      );
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to extend timer by 5 minutes:', error);
    }
  };

  const handleExpandReflectionClick = (e) => {
    e.stopPropagation();

    // On smaller screens, keep using the modal UX
    if (window.innerWidth < 1024) {
      setShowReflectionModal(true);
      return;
    }

    // On desktop, show reflection modal
    setShowReflectionModal(true);
  };

  // Track actual time spent on task
  useEffect(() => {
    if (timerActive && !timerPaused && timerState?.startTime) {
      const interval = setInterval(() => {
        const elapsed = Math.floor((Date.now() - new Date(timerState.startTime).getTime()) / 1000);
        setActualTimeSpent(elapsed);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [timerActive, timerPaused, timerState]);

  // Generate AI reflection for completed task
  const handleGenerateReflection = async () => {
    setIsGeneratingReflection(true);
    try {
      const taskData = {
        userId: activity.user_id,
        taskId: activity.id,
        what: activity.activity_what,
        title: activity.activity_what,
        description:
          activity.task_description ||
          activity.activity_description ||
          activity.description ||
          activity.activity_what,
        priority: activity.priority || 'medium',
        urgencyLevel: activity.urgency_level,
        learningGoal: activity.learning_goal,
        why: activity.activity_why,
        how: activity.activity_how,
        estimatedMinutes: activity.estimated_minutes,
        timeSpent: actualTimeSpent || activity.time_spent || 0,
        scheduledTime: activity.scheduled_time,
        startedAt: activity.started_at,
        completedAt: activity.completed_at,
        completed: activity.completed,
        taskOutcome: 'completed',
        completionPercentage: 100,
      };

      const reflection = await TaskReflectionAgentService.generateQuickReflection(taskData);
      setGeneratedReflection(reflection);

      // Save reflection to database
      await saveTaskReflection(activity.id, reflection);

      // Update time spent if we have it
      if (actualTimeSpent > 0) {
        await updateTaskTimeSpent(activity.id, actualTimeSpent);
      }

      // eslint-disable-next-line no-console
      console.log('Reflection generated and saved:', reflection);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to generate reflection:', error);
      alert('Failed to generate reflection. Please try again.');
    } finally {
      setIsGeneratingReflection(false);
    }
  };

  // Close reflection modal
  const handleCloseReflection = () => {
    setShowReflectionModal(false);
    setGeneratedReflection(null);
    setIsEditingReflection(false);
    setManualReflection('');
  };

  // Save manual reflection
  const handleSaveManualReflection = async () => {
    if (!manualReflection.trim()) {
      alert('Please enter a reflection before saving.');
      return;
    }

    setIsSavingReflection(true);
    try {
      const { error } = await updateActivity(activity.id, {
        reflection: manualReflection,
      });

      if (error) {
        throw error;
      }

      // eslint-disable-next-line no-console
      console.log('Manual reflection saved successfully');
      setIsEditingReflection(false);
      setManualReflection('');

      // Optionally refresh the activity data or show success message
      alert('Reflection saved successfully!');
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to save reflection:', error);
      alert('Failed to save reflection. Please try again.');
    } finally {
      setIsSavingReflection(false);
    }
  };

  // Start editing reflection
  const handleStartEditingReflection = () => {
    setManualReflection(
      normalizeTaskReflectionText(activity.reflection_notes || activity.ai_reflection || '')
    );
    setIsEditingReflection(true);
  };

  // Handle description editing
  const handleStartEditingDescription = (e) => {
    e.stopPropagation();
    setEditedDescription(activity.description || activity.task_description || '');
    setIsEditingDescription(true);
  };

  const handleSaveDescription = async (e) => {
    e.stopPropagation();
    if (editedDescription === (activity.description || activity.task_description || '')) {
      setIsEditingDescription(false);
      return;
    }

    try {
      await updateActivity(activity.id, {
        description: editedDescription,
        taskDescription: editedDescription,
      });
      setIsEditingDescription(false);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to update description:', error);
      alert('Failed to save description. Please try again.');
    }
  };

  const handleCancelEditDescription = (e) => {
    e.stopPropagation();
    setIsEditingDescription(false);
    setEditedDescription('');
  };

  // Handle due date editing
  const handleStartEditingDueDate = (e) => {
    e.stopPropagation();
    // Format date as YYYY-MM-DD for input field
    // Handle both date strings and ISO timestamps
    let currentDate = '';
    if (activity.when) {
      const date = new Date(activity.when);
      // Format as YYYY-MM-DD in local timezone
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      currentDate = `${year}-${month}-${day}`;
    }
    setEditedDueDate(currentDate);
    setIsEditingDueDate(true);
  };

  const handleSaveDueDate = async (e) => {
    e.stopPropagation();
    if (!editedDueDate) {
      setIsEditingDueDate(false);
      return;
    }

    // Compare with current date using same formatting
    let currentDate = '';
    if (activity.when) {
      const date = new Date(activity.when);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      currentDate = `${year}-${month}-${day}`;
    }

    if (editedDueDate === currentDate) {
      setIsEditingDueDate(false);
      return;
    }

    try {
      // Update in database
      await updateActivity(activity.id, {
        when: editedDueDate,
      });

      // Update Redux store immediately for UI responsiveness
      dispatch(
        updateActivityRedux({
          id: activity.id,
          updates: {
            when: editedDueDate,
            task_deadline: editedDueDate,
          },
        })
      );

      setIsEditingDueDate(false);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to update due date:', error);
      alert('Failed to save due date. Please try again.');
    }
  };

  const handleCancelEditDueDate = (e) => {
    e.stopPropagation();
    setIsEditingDueDate(false);
    setEditedDueDate('');
  };

  // Handle priority editing
  const handlePriorityChange = async (e) => {
    e.stopPropagation();
    const newPriority = e.target.value;

    try {
      await updateActivity(activity.id, {
        urgencyLevel: newPriority,
        priority: newPriority,
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to update priority:', error);
      alert('Failed to update priority. Please try again.');
    }
  };

  const formatDueDate = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatTimeRemaining = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hours > 0) {
      return `${hours}h ${minutes}m ${secs}s`;
    }
    if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    }
    return `${secs}s`;
  };

  return (
    <div className={`activity-card-simple ${activity.completed ? 'done' : ''}`}>
      <audio
        ref={audioRef}
        src="https://cdn.pixabay.com/download/audio/2021/08/04/audio_8f3b921f5d.mp3?filename=warning-alarm-6897.mp3"
        preload="auto"
        aria-label="Timer alarm sound"
      >
        <track kind="captions" />
      </audio>
      {/* Active Timer Indicator */}
      {timerActive && !timerPaused && (
        <div className="active-timer-flag">
          <i className="fa-solid fa-circle-play" aria-hidden="true" />
          <span>ACTIVE</span>
        </div>
      )}

      {/* Simple Card Header - checkbox on left */}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onToggleComplete(activity.id);
        }}
        className="activity-checkbox-simple"
        aria-label={activity.completed ? 'Mark as not done' : 'Mark as done'}
      >
        <i
          className={`fa-${activity.completed ? 'solid' : 'regular'} fa-circle-check`}
          aria-hidden="true"
        />
      </button>

      {/* Card Content */}
      <div className="activity-card-content-simple">
        {/* Title */}
        <h3 className="activity-title-simple">{activity.what}</h3>

        {/* Owner */}
        {activity.who && <p className="activity-owner-simple">Owner: {activity.who}</p>}

        {/* Due Date - Editable */}
        <div className="activity-due-date-simple editable-section">
          <div className="section-header">
            <strong>
              <i className="fa-regular fa-calendar" aria-hidden="true" />
              Due Date
            </strong>
            {!isEditingDueDate && !activity.completed && (
              <button
                type="button"
                className="edit-inline-btn"
                onClick={handleStartEditingDueDate}
                aria-label="Edit due date"
              >
                <i className="fa-solid fa-pen" aria-hidden="true" />
              </button>
            )}
          </div>
          {isEditingDueDate ? (
            // eslint-disable-next-line jsx-a11y/no-static-element-interactions
            <div
              className="inline-edit-container"
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.key === 'Escape' && handleCancelEditDueDate(e)}
            >
              <input
                type="date"
                value={editedDueDate}
                onChange={(e) => setEditedDueDate(e.target.value)}
                className="inline-edit-date"
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus
              />
              <div className="inline-edit-actions">
                <button
                  type="button"
                  onClick={handleCancelEditDueDate}
                  className="inline-btn-cancel"
                  aria-label="Cancel"
                >
                  <i className="fa-solid fa-xmark" aria-hidden="true" />
                </button>
                <button
                  type="button"
                  onClick={handleSaveDueDate}
                  className="inline-btn-save"
                  aria-label="Save"
                >
                  <i className="fa-solid fa-check" aria-hidden="true" />
                </button>
              </div>
            </div>
          ) : (
            <span className="date-value">{formatDueDate(activity.when)}</span>
          )}
        </div>

        {/* Estimated Time */}
        {estimatedMinutes > 0 && (
          <div className="activity-estimated-time">
            <i className="fa-regular fa-clock" aria-hidden="true" />
            <span>Estimated Time</span>
            <span className="time-value">
              {estimatedMinutes < 60
                ? `${estimatedMinutes} min`
                : `${Math.floor(estimatedMinutes / 60)}h ${estimatedMinutes % 60}m`}
            </span>
          </div>
        )}

        {/* Description - Editable */}
        <div className="activity-description-simple editable-section">
          <div className="section-header">
            <strong>Description</strong>
            {!isEditingDescription && !activity.completed && (
              <button
                type="button"
                className="edit-inline-btn"
                onClick={handleStartEditingDescription}
                aria-label="Edit description"
              >
                <i className="fa-solid fa-pen" aria-hidden="true" />
              </button>
            )}
          </div>
          {isEditingDescription ? (
            // eslint-disable-next-line jsx-a11y/no-static-element-interactions
            <div
              className="inline-edit-container"
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.key === 'Escape' && handleCancelEditDescription(e)}
            >
              <textarea
                value={editedDescription}
                onChange={(e) => setEditedDescription(e.target.value)}
                className="inline-edit-textarea"
                rows="3"
                placeholder="Add task notes or description..."
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus
              />
              <div className="inline-edit-actions">
                <button
                  type="button"
                  onClick={handleCancelEditDescription}
                  className="inline-btn-cancel"
                  aria-label="Cancel"
                >
                  <i className="fa-solid fa-xmark" aria-hidden="true" />
                </button>
                <button
                  type="button"
                  onClick={handleSaveDescription}
                  className="inline-btn-save"
                  aria-label="Save"
                >
                  <i className="fa-solid fa-check" aria-hidden="true" />
                </button>
              </div>
            </div>
          ) : (
            <p className="description-text">
              {activity.description || activity.task_description || 'Click edit to add notes...'}
            </p>
          )}
        </div>

        {/* Priority - Editable (disabled for completed tasks) */}
        <div className="activity-priority-simple editable-section">
          <strong>Priority</strong>
          <select
            value={activity.urgencyLevel || 'medium'}
            onChange={handlePriorityChange}
            onClick={(e) => e.stopPropagation()}
            className="priority-select"
            aria-label="Change priority"
            disabled={activity.completed}
          >
            <option value="low">🟢 Low</option>
            <option value="medium">🟡 Medium</option>
            <option value="high">🟠 High</option>
            <option value="critical">🔴 Critical</option>
          </select>
        </div>

        {/* Reflection Notes - Only show for completed tasks with reflection */}
        {activity.completed && (activity.reflection_notes || activity.ai_reflection) && (
          <div className="activity-reflection-simple">
            <strong>
              <i className="fa-solid fa-lightbulb" aria-hidden="true" />
              Reflection
            </strong>
            <p className="reflection-preview">
              {activity.ai_reflection || activity.reflection_notes}
            </p>
            <button
              type="button"
              className="view-full-reflection-btn"
              onClick={handleExpandReflectionClick}
            >
              Expand
            </button>
          </div>
        )}

        {/* Project/Course Tags - show human-readable titles if available */}
        {(activity.projectId || activity.courseId) && (
          <div className="activity-tags-simple">
            {activity.projectId && (
              <span className="tag-simple project-tag-simple">
                <i className="fa-solid fa-diagram-project" aria-hidden="true" />
                {linkedProjectName || activity.projectId}
              </span>
            )}
            {activity.courseId && (
              <span className="tag-simple course-tag-simple">
                <i className="fa-solid fa-graduation-cap" aria-hidden="true" />
                {linkedCourseName || activity.courseId}
              </span>
            )}
          </div>
        )}

        {/* Timer & controls - disabled for completed tasks */}
        {!activity.completed && (
          <>
            {/* Timer Display */}
            {timerActive && timeRemainingSeconds !== null && (
              <div className="timer-display-simple">
                <i className="fa-solid fa-clock" aria-hidden="true" />
                <span className={timeRemainingSeconds <= 60 ? 'timer-warning' : ''}>
                  {formatTimeRemaining(timeRemainingSeconds)}
                </span>
              </div>
            )}

            {/* Stop Alarm + Add 5 minutes - only when time ran out */}
            {needsAttention && timeRemainingSeconds !== null && timeRemainingSeconds <= 0 && (
              <div className="alarm-controls">
                <button
                  type="button"
                  className="edit-btn-simple alarm-stop-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleStopAlarm();
                  }}
                  aria-label="Stop alarm"
                  title="Stop alarm"
                >
                  <i className="fa-solid fa-bell-slash" aria-hidden="true" />
                </button>
                <button
                  type="button"
                  className="edit-btn-simple"
                  onClick={handleExtendFiveMinutes}
                  aria-label="Add 5 minutes"
                  title="Add 5 minutes"
                >
                  <i className="fa-solid fa-plus" aria-hidden="true" />
                </button>
              </div>
            )}

            {/* Main Action Icons - Play/Pause, AI, and Delete */}
            <div className="task-action-icons">
              <button
                type="button"
                className="task-icon-btn play-pause-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  if (!timerActive) {
                    handleStartTimer();
                  } else if (timerPaused) {
                    handleResumeTimer();
                  } else {
                    handlePauseTimer();
                  }
                }}
                aria-label={
                  !timerActive ? 'Start task' : timerPaused ? 'Resume task' : 'Pause task'
                }
                title={!timerActive ? 'Start task' : timerPaused ? 'Resume task' : 'Pause task'}
              >
                <i
                  className={`fa-solid fa-${!timerActive || timerPaused ? 'play' : 'pause'}`}
                  aria-hidden="true"
                />
              </button>

              <button
                type="button"
                className="task-icon-btn ai-focus-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  openAgentModal({
                    agentId: 'tasks',
                    origin: 'task-card',
                    contextPayload: {
                      taskId: activity.id,
                      taskTitle: activity.what || activity.activity_what,
                      taskDescription: activity.how || activity.activity_how,
                      dueDate: activity.due_date,
                      estimatedMinutes: activity.estimated_minutes,
                      isRunning: timerActive && !timerPaused,
                      projectId: activity.projectId,
                      courseId: activity.courseId,
                      completed: activity.completed,
                      focusMode: true,
                    },
                  });
                }}
                aria-label="Focus AI on this task"
                title="Ask AI about this specific task"
              >
                <i className="fa-solid fa-brain" aria-hidden="true" />
              </button>

              <button
                type="button"
                className="task-icon-btn delete-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  if (window.confirm('Are you sure you want to delete this task?')) {
                    onDelete(activity.id);
                  }
                }}
                aria-label="Delete task"
                title="Delete task"
              >
                <i className="fa-solid fa-trash" aria-hidden="true" />
              </button>
            </div>
          </>
        )}
      </div>

      {!activity.completed && (
        <div className="timer-controls-expanded">
          <div className="timer-controls">
            {/* Finish button - only show when timer is active */}
            {timerActive && (
              <button
                type="button"
                onClick={handleFinishTimer}
                className="timer-btn finish-btn"
                aria-label="Mark as done"
                title="Mark as done"
              >
                <i className="fa-solid fa-check" aria-hidden="true" />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Time Input Modal */}
      {showTimeInput && (
        <div className="time-input-modal">
          <div className="time-input-content">
            <i className="fa-solid fa-clock time-input-icon" aria-label="Estimate time" />
            <div className="time-input-field">
              <input
                type="number"
                value={estimatedMinutesInput}
                onChange={(e) => setEstimatedMinutesInput(e.target.value)}
                placeholder="Minutes (optional)"
                min="1"
                aria-label="Estimated minutes"
              />
            </div>
            <div className="time-input-actions">
              <button type="button" onClick={() => setShowTimeInput(false)} className="btn-cancel">
                <i className="fa-solid fa-xmark" aria-hidden="true" />
              </button>
              <button type="button" onClick={handleConfirmStart} className="btn-start">
                <i className="fa-solid fa-play" aria-hidden="true" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Hidden Audio Element for Timer Alarm - Beeping Sound */}
      <audio
        ref={audioRef}
        src="https://assets.mixkit.co/active_storage/sfx/2869/2869-preview.mp3"
        preload="auto"
        aria-label="Timer alarm beeping sound"
      >
        <track kind="captions" />
      </audio>

      {/* Reflection Modal */}
      {showReflectionModal && (
        <div className="reflection-modal-overlay">
          <button
            type="button"
            className="reflection-modal-backdrop"
            onClick={handleCloseReflection}
            aria-label="Close reflection modal"
          >
            {/* Backdrop button for closing */}
          </button>
          <div className="reflection-modal-content">
            <div className="reflection-modal-header">
              <h3>
                <i className="fa-solid fa-lightbulb" aria-hidden="true" />
                Task Reflection
              </h3>
              <button
                type="button"
                onClick={handleCloseReflection}
                className="reflection-close-btn"
                aria-label="Close reflection"
              >
                <i className="fa-solid fa-xmark" aria-hidden="true" />
              </button>
            </div>

            <div className="reflection-modal-body">
              {isGeneratingReflection && (
                <div className="reflection-loading">
                  <i className="fa-solid fa-spinner fa-spin" aria-hidden="true" />
                  <p>Generating your reflection...</p>
                </div>
              )}

              {/* Manual Reflection Editor */}
              {isEditingReflection && (
                <div className="reflection-section">
                  <h4>
                    <i className="fa-solid fa-pen" aria-hidden="true" />
                    Edit Reflection
                  </h4>
                  <textarea
                    className="reflection-textarea"
                    value={manualReflection}
                    onChange={(e) => setManualReflection(e.target.value)}
                    placeholder="Write your reflection about this task..."
                    rows={8}
                    disabled={isSavingReflection}
                  />
                  <div className="reflection-edit-actions">
                    <button
                      type="button"
                      onClick={() => {
                        setIsEditingReflection(false);
                        setManualReflection('');
                      }}
                      className="reflection-cancel-btn"
                      disabled={isSavingReflection}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleSaveManualReflection}
                      className="reflection-save-btn"
                      disabled={isSavingReflection || !manualReflection.trim()}
                    >
                      {isSavingReflection ? (
                        <>
                          <i className="fa-solid fa-spinner fa-spin" aria-hidden="true" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <i className="fa-solid fa-save" aria-hidden="true" />
                          Save Reflection
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}

              {!isGeneratingReflection &&
                !isEditingReflection &&
                (generatedReflection || activity.ai_reflection || activity.reflection_notes) && (
                  <>
                    <div className="reflection-section">
                      <div className="reflection-header-with-edit">
                        <h4>Reflection</h4>
                        <button
                          type="button"
                          onClick={handleStartEditingReflection}
                          className="reflection-edit-btn"
                          title="Edit reflection"
                        >
                          <i className="fa-solid fa-pen" aria-hidden="true" />
                          Edit
                        </button>
                      </div>
                      <FormattedReflection
                        text={
                          generatedReflection?.aiReflection ||
                          activity.ai_reflection ||
                          activity.reflection_notes
                        }
                      />
                    </div>

                    {(generatedReflection?.insights || activity.reflection_insights) && (
                      <div className="reflection-section">
                        <h4>
                          <i className="fa-solid fa-star" aria-hidden="true" />
                          Key Insights
                        </h4>
                        <ul className="reflection-list">
                          {(
                            generatedReflection?.insights ||
                            activity.reflection_insights ||
                            []
                          ).map((insight, idx) => (
                            <li key={idx}>{insight}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {(generatedReflection?.patterns || activity.reflection_patterns) && (
                      <div className="reflection-section">
                        <h4>
                          <i className="fa-solid fa-chart-line" aria-hidden="true" />
                          Patterns Observed
                        </h4>
                        <ul className="reflection-list">
                          {(
                            generatedReflection?.patterns ||
                            activity.reflection_patterns ||
                            []
                          ).map((pattern, idx) => (
                            <li key={idx}>{pattern}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {(generatedReflection?.recommendations ||
                      activity.reflection_recommendations) && (
                      <div className="reflection-section">
                        <h4>
                          <i className="fa-solid fa-arrow-up-right-dots" aria-hidden="true" />
                          Recommendations
                        </h4>
                        <ul className="reflection-list">
                          {(
                            generatedReflection?.recommendations ||
                            activity.reflection_recommendations ||
                            []
                          ).map((rec, idx) => (
                            <li key={idx}>{rec}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {(generatedReflection?.keyLearnings || activity.key_learnings) && (
                      <div className="reflection-section">
                        <h4>
                          <i className="fa-solid fa-book" aria-hidden="true" />
                          Key Learnings
                        </h4>
                        <ul className="reflection-list">
                          {(generatedReflection?.keyLearnings || activity.key_learnings || []).map(
                            (learning, idx) => (
                              <li key={idx}>{learning}</li>
                            )
                          )}
                        </ul>
                      </div>
                    )}

                    {actualTimeSpent > 0 && (
                      <div className="reflection-section">
                        <h4>
                          <i className="fa-solid fa-clock" aria-hidden="true" />
                          Time Tracking
                        </h4>
                        <p>
                          Time spent: {Math.floor(actualTimeSpent / 60)} minutes{' '}
                          {actualTimeSpent % 60} seconds
                        </p>
                        {activity.estimated_minutes && (
                          <p>
                            Estimated: {activity.estimated_minutes} minutes
                            {actualTimeSpent / 60 > activity.estimated_minutes
                              ? ` (${Math.round(actualTimeSpent / 60 - activity.estimated_minutes)} min over)`
                              : ` (${Math.round(activity.estimated_minutes - actualTimeSpent / 60)} min under)`}
                          </p>
                        )}
                      </div>
                    )}
                  </>
                )}
            </div>

            <div className="reflection-modal-footer">
              <button
                type="button"
                onClick={handleCloseReflection}
                className="reflection-close-footer-btn"
              >
                Close
              </button>
              {!isGeneratingReflection && !generatedReflection && !activity.ai_reflection && (
                <button
                  type="button"
                  onClick={handleGenerateReflection}
                  className="reflection-generate-btn"
                >
                  <i className="fa-solid fa-lightbulb" aria-hidden="true" />
                  Generate Reflection
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ActivityCard;
